/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { OrchestrationDeployModelModal } from 'src/clu/appShell/cluDeployModelPage/components/OrchestrationDeployModelModal';
import { getCluPredictionCurlRequest, getCluPredictionUrl } from 'src/clu/core/services/test/cluTestModelService';
import { useAuthKeys } from 'src/core/hooks/useAuthKeys';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { ResourceAssignmentDto, ResourceAssignmentForRegionSwagger, ResourceDto } from 'src/core/services/resourceAssignmentSwaggers';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';
import { getEnvironment } from 'src/core/utils/api/environment';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { getResourceInfo } from 'src/core/utils/resourceAssignmentUtils';
import { useAzureResourcesManagement } from 'src/modules/azure/hooks/useAzureResourcesManagement';
import { ModelDeployLayout } from 'src/modules/modelDeploy/ModelDeployLayout';

const messages = defineMessages({
    deploymentFailed: { id: 'clu.cluDeployModelPage.deploymentFailed', defaultMessage: 'Deployment failed' },
    deploymentCompleted: { id: 'clu.cluDeployModelPage.deploymentCompleted', defaultMessage: 'Deployment completed' },
    deleteDeploymentFailed: { id: 'clu.cluDeployModelPage.deleteDeploymentFailed', defaultMessage: 'Delete deployment failed' },
    deploymentInProgress: { id: 'clu.cluDeployModelPage.deploymentInProgress', defaultMessage: 'Deploying "{deploymentName}"...' },
    deleteDeploymentCompleted: { id: 'clu.cluDeployModelPage.deleteDeploymentCompleted', defaultMessage: 'Delete deployment completed' },
    deleteDeploymentInProgress: {
        id: 'clu.cluDeployModelPage.deleteDeploymentInProgress',
        defaultMessage: 'Deleting "{deploymentName}"...'
    },
    addResourceAssignmentCompleted: {
        id: 'clu.cluDeployModelPage.addResourceAssignmentCompleted',
        defaultMessage: 'Resource is assigned successfully.'
    },
    addResourceAssignmentFailed: {
        id: 'clu.cluDeployModelPage.addResourceAssignmentFailed',
        defaultMessage: 'Resource assignment failed.'
    },
    editResourceAssignmentCompleted: {
        id: 'clu.cluDeployModelPage.editResourceAssignmentCompleted',
        defaultMessage: 'Resource assignment is updated successfully.'
    },
    editResourceAssignmentFailed: {
        id: 'clu.cluDeployModelPage.editResourceAssignmentFailed',
        defaultMessage: 'Updating Resource assignment failed.'
    },
    removeResourceAssignmentCompleted: {
        id: 'clu.cluDeployModelPage.removeResourceAssignmentCompleted',
        defaultMessage: 'Resource assignment is removed successfully.'
    },
    removeResourceAssignmentFailed: {
        id: 'clu.cluDeployModelPage.removeResourceAssignmentFailed',
        defaultMessage: 'Removing Resource assignment failed.'
    }
});

export const CluDeployModelPage = observer(() => {
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const deleteTracker = useProgressTracker({ onComplete: () => setDeletedDeploymentsIds([]) });

    const [deletedDeploymentsIds, setDeletedDeploymentsIds] = React.useState([]);

    const [currentResourceDto, updateCurrentResourceDto] = useUpdateState<ResourceDto>({
        resource: rootStore.azureStore.session.csAccountStore,
        resourceId: rootStore.azureStore.session.csAccountStore.path
    });
    const [selectedResourceSubscriptionId, setSelectedResourceSubscriptionId] = React.useState<string>(null);
    const { resources, resourcesTracker } = useAzureResourcesManagement({
        configs: { resources: true },
        subscriptionId: selectedResourceSubscriptionId
    });

    const {
        projectType,
        intentStoreHouse,
        name: projectName,
        deploymentsStoreHouse,
        trainedModelStoreHouse,
        resourceAssignmentsStoreHouse
    } = rootStore.currentCluProjectStore;

    const isOrchestrationProjectConnectedToLUISOrQna = React.useMemo(
        () =>
            projectType === 'Orchestration' &&
            Boolean(
                intentStoreHouse.items.find(item => item.orchestrationType === 'Luis' || item.orchestrationType === 'QuestionAnswering')
            ),
        [projectType, intentStoreHouse.items.length]
    );

    const { authorizationHeader } = useAuthKeys({
        currentServiceKey: rootStore.azureStore.session.serviceKey,
        currentResource: rootStore.azureStore.session.csAccountStore
    });

    const endpointUrl = React.useCallback(
        () =>
            getCluPredictionUrl(getEnvironment, {
                currentResourceKind: currentResourceDto.resource.kind,
                currentResourceEndpoint: currentResourceDto.resource.endpoint
            }),
        [currentResourceDto.resource]
    );

    React.useEffect(() => {
        if (resourcesTracker.isCompleted) {
            updateCurrentResourceDto({ resource: resources.find(resource => resource.path === currentResourceDto.resourceId) });
        }
    }, [currentResourceDto.resourceId, resourcesTracker.state]);

    React.useEffect(() => {
        dispatcher.dispatch('getCluDeployments', {
            projectName,
            deploymentsStoreHouse,
            trainedModelStoreHouse
        });
        if (FeatureToggles.MultiRegionDeployments) {
            dispatcher.dispatch('getCluProjectAssignedResources');
        }
    }, []);

    const submitCode = React.useCallback(
        (deploymentName: string) =>
            getCluPredictionCurlRequest({
                projectName,
                getEnvironment,
                environmentOptions: {
                    currentResourceKind: currentResourceDto.resource.kind,
                    currentResourceEndpoint: currentResourceDto.resource.endpoint
                },
                authorizationHeader,
                deploymentName
            }),
        [currentResourceDto.resource, getEnvironment, projectName, authorizationHeader.key, authorizationHeader.value]
    );

    const deploy = (deploymentInfo: {
        modelName: string;
        deploymentName: string;
        tracker: ProgressTrackerStore;
        resources: ResourceAssignmentForRegionSwagger[];
    }) => {
        dispatcher.dispatch('deployCluModel', {
            tracker: deploymentInfo.tracker,
            trainedModelName: deploymentInfo.modelName,
            deploymentName: deploymentInfo.deploymentName,
            assignedResourceIds: deploymentInfo.resources.map(resource => resource.azureResourceId),
            toastData: {
                failed: { title: localize(messages.deploymentFailed) },
                completed: { title: localize(messages.deploymentCompleted) },
                running: { title: localize(messages.deploymentInProgress, { deploymentName: deploymentInfo.deploymentName }) }
            }
        });
    };

    const handleDeploymentsSwap = (sourceDeploymentName: string, targetDeploymentName: string, tracker: ProgressTrackerStore) =>
        dispatcher.dispatch('swapCluDeployments', { sourceDeploymentName, targetDeploymentName, tracker });

    const handleDeleteDeployment = (deploymentName: string) => {
        dispatcher.dispatch('deleteCLuDeployment', {
            deploymentName,
            tracker: deleteTracker,
            toastData: {
                failed: { title: localize(messages.deleteDeploymentFailed) },
                completed: { title: localize(messages.deleteDeploymentCompleted) },
                running: { title: localize(messages.deleteDeploymentInProgress, { deploymentName }) }
            }
        });

        setDeletedDeploymentsIds([...deletedDeploymentsIds, deploymentName]);
    };

    const handleAddResourceAssignment = (assignmentData: ResourceAssignmentDto, tracker: ProgressTrackerStore) => {
        dispatcher.dispatch('addCluProjectResourceAssignment', {
            tracker,
            assignmentData,
            toastData: {
                failed: { title: localize(messages.addResourceAssignmentFailed) },
                completed: { title: localize(messages.addResourceAssignmentCompleted) }
            }
        });
    };

    const handleUpdateResourceAssignment = (
        assignmentData: ResourceAssignmentDto,
        oldResourceId: string,
        tracker: ProgressTrackerStore
    ) => {
        dispatcher.dispatch('updateCluProjectResourceAssignment', {
            tracker,
            oldResourceId,
            assignmentData,
            toastData: {
                failed: { title: localize(messages.editResourceAssignmentFailed) },
                completed: { title: localize(messages.editResourceAssignmentCompleted) }
            }
        });
    };

    const handleRemoveResourceAssignment = (assignmentData: Partial<ResourceAssignmentStore>, tracker: ProgressTrackerStore) => {
        dispatcher.dispatch('deleteCluProjectResourceAssignment', {
            tracker,
            assignmentData,
            toastData: {
                failed: { title: localize(messages.removeResourceAssignmentFailed) },
                completed: { title: localize(messages.removeResourceAssignmentCompleted) }
            }
        });
    };

    const onChangeSelectedResource = (id: string) => {
        const { subscriptionId } = getResourceInfo(id);
        setSelectedResourceSubscriptionId(subscriptionId);
        updateCurrentResourceDto({ resourceId: id });
    };

    return (
        <ModelDeployLayout
            onDeploySubmit={deploy}
            submitCode={submitCode}
            predictUrl={endpointUrl}
            projectName={projectName}
            projectType={projectType}
            onSwapDeployments={handleDeploymentsSwap}
            onDeleteDeployment={handleDeleteDeployment}
            isServiceKeyTypeValid
            deletedDeploymentsIds={deletedDeploymentsIds}
            deploymentsStoreHouse={deploymentsStoreHouse}
            isLoadingResources={resourcesTracker.isRunning}
            trainedModelStoreHouse={trainedModelStoreHouse}
            onChangeSelectedResource={onChangeSelectedResource}
            onAddResourceAssignment={handleAddResourceAssignment}
            onEditResourceAssignment={handleUpdateResourceAssignment}
            regionDeploymentStoreHouse={resourceAssignmentsStoreHouse}
            onRemoveResourceAssignment={handleRemoveResourceAssignment}
            isOrchestrationProjectConnectedToLUISOrQna={isOrchestrationProjectConnectedToLUISOrQna}
            onRenderDeployModal={
                projectType === 'Orchestration' &&
                (modalProps => (
                    <OrchestrationDeployModelModal
                        isServiceKeyTypeValid
                        isOrchestrationProjectConnectedToLUISOrQna={isOrchestrationProjectConnectedToLUISOrQna}
                        {...modalProps}
                    />
                ))
            }
        />
    );
});
